import Footer from "@/components/Footer";
import GenericTable from "@/components/GenericTable";
import { MERCHANT_BILL_TABLE_COLUMNS } from "@/config/tables";
import { useTableFiltersCursor } from "@/hooks/useTableFiltersCursor";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import { MerchantBill } from "@/types/types";
import { Badge, Box, Button, Card, Flex, Table, Text } from "@chakra-ui/react";
import { ExternalLink, Wallet } from "lucide-react";
import CommonLayout from "../CommonLayout";
import Filters from "./components/Filters";
import { useFetchMerchantBills } from "./hooks/useFetchMerchantBills";
import { FiltersType } from "./types/filters.type";

const PAYMENTS_PORTAL_URL = "https://pagos.palomma.com/palomma";
const TABLE_KEY = "merchant-bills";

const BillingContainer = (): JSX.Element => {
  const { queryFilters, setQueryFilters } =
    useTableFiltersCursor<FiltersType>(TABLE_KEY);
  const { bills, totalOutstanding, isLoading } =
    useFetchMerchantBills(queryFilters);
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");

  const handlePay = (bill: MerchantBill) => {
    const url = `${PAYMENTS_PORTAL_URL}?cliente=${encodeURIComponent(
      bill.merchantInfo.documentNumber
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const outstandingCard = (
    <Card.Root
      bg="white"
      rounded="lg"
      border="none"
      boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
      p={4}
      maxW={{ base: "100%", md: "320px" }}
    >
      <Card.Body p={0} display="flex" flexDirection="column">
        <Flex justifyContent="space-between" alignItems="center" mb={1}>
          <Text fontSize="14px" fontWeight="600" color="#54596C">
            Saldo pendiente
          </Text>
          <Flex
            alignItems="center"
            justifyContent="center"
            w="32px"
            h="32px"
            bg="#F3F5FC"
            borderRadius="md"
          >
            <Wallet size={16} color="#A7ACB8" />
          </Flex>
        </Flex>
        <Text fontSize="24px" fontWeight="700" color="#1A202C">
          {formatCurrency(totalOutstanding)}
        </Text>
      </Card.Body>
    </Card.Root>
  );

  const table = (
    <Box display="flex" flexDirection="column" gap="1.5rem" flex="1" minH={0}>
      <Filters queryFilters={queryFilters} setQueryFilters={setQueryFilters} />
      <GenericTable
        isLoading={isLoading}
        columns={MERCHANT_BILL_TABLE_COLUMNS}
        fillHeight
      >
        {bills.map((bill) => (
          <Table.Row key={bill.id}>
            <Table.Cell color="gray.600">{bill.period}</Table.Cell>
            <Table.Cell color="gray.600">{bill.dueDate}</Table.Cell>
            <Table.Cell>
              <Badge
                colorPalette={bill.type === "prepaid" ? "green" : "blue"}
                variant="subtle"
              >
                {bill.type === "prepaid" ? "Descontada" : "Mes Vencido"}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              <Text fontWeight="bold" color="gray.800">
                {formatCurrency(bill.amount)}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Badge
                colorPalette={bill.paid ? "green" : "orange"}
                variant="subtle"
              >
                {bill.paid ? "Pagado" : "Sin Pagar"}
              </Badge>
            </Table.Cell>
            <Table.Cell py={0}>
              {!bill.paid && (
                <Button
                  size="xs"
                  h="26px"
                  px={3}
                  colorPalette="palomma"
                  variant="outline"
                  fontWeight="medium"
                  gap={1}
                  onClick={() => handlePay(bill)}
                >
                  <ExternalLink size={14} />
                  Pagar
                </Button>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </GenericTable>
      <Box mt="auto" pt={6} flexShrink={0}>
        <Footer
          tableProps={{
            fileName: "facturacion",
            items: bills,
            columns: MERCHANT_BILL_TABLE_COLUMNS,
          }}
        />
      </Box>
    </Box>
  );

  return (
    <CommonLayout
      title="Facturación"
      subtitle="Consulta tu saldo pendiente y el historial de facturas de Palomma"
      buttons={<></>}
      children={outstandingCard}
      table={table}
    />
  );
};

export default BillingContainer;
